import type { SsoSilentRequest } from "@azure/msal-browser";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { msalInstance } from "../../../config/authConfig";
import apiClient from "../../../config/axiosConfig";
import { authPersistence } from "../../../utils/authPersistence";
import {
	clearTokenRefresh,
	setupTokenRefresh,
} from "../../../utils/tokenRefresh";

export type Role = string;

export interface User {
	id: string;
	email: string;
	name: string;
	roles: Role[]; // Update roles to be an array of Role objects
}

export interface AuthState {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: null | User;
}

export const initializeAuth = createAsyncThunk(
	"auth/initializeAuth",
	async () => {
		// First check if we have an active account (from MSAL initialization)
		let account = msalInstance.getActiveAccount();

		// If no active account, check all accounts
		if (!account) {
			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				account = accounts[0];
				msalInstance.setActiveAccount(account);
			}
		}

		// Try silent SSO even if we don't yet have an active account
		const savedUsernameSymbol = Symbol.for("__msal_saved_username");
		const savedUsername = (
			window as unknown as Record<string, string | undefined>
		)[savedUsernameSymbol as unknown as string] as string | undefined;
		if (!account) {
			try {
				const ssoParams: SsoSilentRequest = savedUsername
					? {
							scopes: ["user.read", "profile", "openid", "email"],
							loginHint: savedUsername,
						}
					: {
							scopes: ["user.read", "profile", "openid", "email"],
						};

				const tokenResponse = await msalInstance.ssoSilent(ssoParams);

				if (tokenResponse.account) {
					account = tokenResponse.account;
					msalInstance.setActiveAccount(account);
					authPersistence.saveAccount(account);
				}
			} catch (silentError: unknown) {
				// Silent login failed, user will need to login interactively
				console.debug(
					"[Auth] Silent SSO failed, user needs to login interactively",
				);
			} finally {
				// Clean up the saved username from window object
				if (savedUsername) {
					delete (window as unknown as Record<string, unknown>)[
						savedUsernameSymbol as unknown as string
					];
				}
			}
		}

		if (account) {
			try {
				// Try to acquire token silently
				const tokenResponse = await msalInstance.acquireTokenSilent({
					scopes: ["user.read", "profile", "openid", "email"],
					account,
					forceRefresh: false, // Use cached token if available
				});

				// Set the authorization header
				apiClient.defaults.headers.common["Authorization"] =
					`Bearer ${tokenResponse.accessToken}`;

				// Fetch user roles from the backend
				const rolesResponse = await apiClient.get<{ roles: Role[] }>(
					"/users/roles",
				);
				if (!rolesResponse.data) {
					throw new Error("No roles found");
				}
				const roles = rolesResponse.data.roles;

				return {
					account: account,
					token: tokenResponse,
					roles: roles, // Include roles in the returned payload
				};
			} catch (error: unknown) {
				// Check if this is an interaction required error
				if (error?.name === "InteractionRequiredAuthError") {
					// Don't throw, just return null to indicate auth needed
					throw new Error("Interactive login required");
				}
				// Log only non-sensitive error information
				console.error("[Auth] Token acquisition failed");
				throw error;
			}
		} else {
			// No account found - user needs to login
			throw new Error("No active account found.");
		}
	},
);

// Add a logout thunk that properly handles MSAL logout
export const logoutUser = createAsyncThunk("auth/logout", async () => {
	try {
		// Clear token refresh timer
		clearTokenRefresh();

		// Set a flag to indicate logout is in progress
		sessionStorage.setItem("msal_logout_in_progress", "true");

		// Clear the authorization header
		delete apiClient.defaults.headers.common["Authorization"];

		// Clear our custom persistence
		authPersistence.clearAccount();

		// Clear all accounts from MSAL cache
		const accounts = msalInstance.getAllAccounts();
		if (accounts.length > 0) {
			// Clear active account
			msalInstance.setActiveAccount(null);
		}

		// Clear localStorage but preserve the logout flag in sessionStorage
		localStorage.clear();

		// Logout from MSAL - this will clear server-side session
		await msalInstance.logoutRedirect({
			postLogoutRedirectUri: window.location.origin + "/login",
			account: accounts[0], // Specify which account to logout
		});
	} catch (error) {
		console.error("Logout failed:", error);
		// Even if logout fails, clear local state
		sessionStorage.setItem("msal_logout_in_progress", "true");
		localStorage.clear();
		authPersistence.clearAccount();
		// Force redirect to login
		window.location.href = "/login";
	}
});

const authSlice = createSlice({
	name: "auth",
	initialState: {
		isAuthenticated: false,
		isLoading: true,
		user: null,
	} as AuthState,
	reducers: {
		logout: (state) => {
			state.isAuthenticated = false;
			state.user = null;
			localStorage.clear(); // Clear all local storage
			clearTokenRefresh(); // Clear token refresh timer
		},
		setLoadingComplete: (state) => {
			state.isLoading = false;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(initializeAuth.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(initializeAuth.fulfilled, (state, action) => {
			state.isAuthenticated = true;
			state.isLoading = false;
			state.user = {
				id: action.payload.account.localAccountId,
				email: action.payload.account.username,
				name: action.payload.account.name || "",
				roles: action.payload.roles, // Store roles in the state
			};
			// Set up proactive token refresh on successful authentication
			setupTokenRefresh();
		});
		builder.addCase(initializeAuth.rejected, (state) => {
			state.isAuthenticated = false;
			state.isLoading = false;
			state.user = null;
		});
	},
});

export const { logout, setLoadingComplete } = authSlice.actions;
export default authSlice.reducer;
